import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';

/**
 * Form Library — audit view over every form template in the database.
 *
 * - Shows ALL templates, including archived (inactive) ones
 * - Groups likely duplicates by normalized name so they can be cleaned up
 * - Archive / Restore only (reversible is_active toggle) — there is NO delete here
 */

const normalizeName = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/\(copy\)|\bcopy\b|\bfinal\b|\bnew\b|\bv\d+\b/g, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();

const isReady = (t) => !!(t.blank_file_path || t.blank_file_url);

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'duplicates', label: 'Possible duplicates' },
  { id: 'needs_setup', label: 'Needs setup' },
  { id: 'archived', label: 'Archived' },
];

const FormLibrary = ({ onClose, onEditFields }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getFormTemplates();
      setTemplates(data || []);
    } catch (err) {
      console.error('Form library load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const duplicateKeys = useMemo(() => {
    const counts = {};
    templates.forEach(t => {
      const key = normalizeName(t.name);
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter(k => counts[k] > 1));
  }, [templates]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = templates.filter(t => !q || (t.name || '').toLowerCase().includes(q));
    if (filter === 'duplicates') list = list.filter(t => duplicateKeys.has(normalizeName(t.name)));
    else if (filter === 'needs_setup') list = list.filter(t => !isReady(t));
    else if (filter === 'archived') list = list.filter(t => t.is_active === false);
    return list;
  }, [templates, search, filter, duplicateKeys]);

  // Group: duplicate clusters first (sorted by name), then everything else alphabetically
  const { dupGroups, singles } = useMemo(() => {
    const groups = {};
    const rest = [];
    visible.forEach(t => {
      const key = normalizeName(t.name);
      if (duplicateKeys.has(key)) {
        (groups[key] = groups[key] || []).push(t);
      } else {
        rest.push(t);
      }
    });
    const dupGroups = Object.values(groups)
      .map(g => g.sort((a, b) => (a.name || '').localeCompare(b.name || '')))
      .sort((a, b) => (a[0].name || '').localeCompare(b[0].name || ''));
    rest.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return { dupGroups, singles: rest };
  }, [visible, duplicateKeys]);

  const handleToggleArchive = async (template) => {
    const archiving = template.is_active !== false;
    if (archiving && !window.confirm(
      `Archive "${template.name}"?\n\nIt will stop printing and be hidden from packs, but nothing is deleted — you can restore it here anytime.`
    )) return;
    setBusyId(template.id);
    try {
      await api.updateFormTemplate(template.id, { is_active: !archiving ? true : false });
      await load();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleRename = async (template) => {
    const name = window.prompt('Rename form:', template.name);
    if (!name || !name.trim() || name.trim() === template.name) return;
    setBusyId(template.id);
    try {
      await api.updateFormTemplate(template.id, { name: name.trim() });
      await load();
    } catch (err) {
      alert('Failed to rename: ' + err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handlePreview = (template) => {
    window.open(api.getBlankTemplatePdfUrl ? api.getBlankTemplatePdfUrl(template.id) : api.getFormTemplatePdfUrl(template.id), '_blank');
  };

  // Permanent delete — ONLY offered on archived forms (two-step safety), double confirm.
  // The server enforces the archived-first rule too.
  const handleDeleteForever = async (template) => {
    if (!window.confirm(`PERMANENTLY delete "${template.name}"?\n\nThis removes the form and its uploaded file forever. It cannot be undone.`)) return;
    if (!window.confirm(`Last check: delete "${template.name}" forever?`)) return;
    setBusyId(template.id);
    try {
      await api.deleteFormTemplate(template.id);
      await load();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    } finally {
      setBusyId(null);
    }
  };

  const archivedCount = templates.filter(t => t.is_active === false).length;
  const dupCount = templates.filter(t => duplicateKeys.has(normalizeName(t.name))).length;

  const renderRow = (t) => (
    <div
      key={t.id}
      className={`flex items-center gap-2.5 px-3 py-2 border rounded-lg ${
        t.is_active === false ? 'bg-neutral-50 border-neutral-200 opacity-70' : 'bg-white border-neutral-200'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${t.is_active === false ? 'line-through text-neutral-400' : ''}`}>{t.name}</span>
          {t.is_active === false && <span className="text-[10px] bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded whitespace-nowrap">Archived</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span className="text-[10px] text-neutral-400">{t.form_type}</span>
          {(Array.isArray(t.packs) ? t.packs : []).map(p => (
            <span key={p.id} className="text-[9px] bg-blue-50 text-blue-600 px-1 py-0.5 rounded whitespace-nowrap">{p.name}</span>
          ))}
          {(!t.packs || t.packs.length === 0) && (
            <span className="text-[9px] bg-neutral-100 text-neutral-400 px-1 py-0.5 rounded">No pack</span>
          )}
        </div>
      </div>
      {isReady(t) ? (
        <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded whitespace-nowrap">Ready</span>
      ) : (
        <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded whitespace-nowrap" title="No print-ready file — this form will not print until it's set up">Needs setup</span>
      )}
      <div className="flex gap-1 flex-shrink-0">
        <button onClick={() => handlePreview(t)} className="px-2 py-1 text-[11px] border rounded hover:bg-neutral-50">Preview</button>
        {onEditFields && (
          <button onClick={() => onEditFields(t)} className="px-2 py-1 text-[11px] border rounded hover:bg-neutral-50">Fields</button>
        )}
        <button onClick={() => handleRename(t)} disabled={busyId === t.id} className="px-2 py-1 text-[11px] border rounded hover:bg-neutral-50 disabled:opacity-40">Rename</button>
        <button
          onClick={() => handleToggleArchive(t)}
          disabled={busyId === t.id}
          className={`px-2 py-1 text-[11px] border rounded disabled:opacity-40 ${
            t.is_active === false
              ? 'border-green-300 text-green-700 hover:bg-green-50'
              : 'border-amber-300 text-amber-700 hover:bg-amber-50'
          }`}
        >
          {t.is_active === false ? 'Restore' : 'Archive'}
        </button>
        {t.is_active === false && (
          <button
            onClick={() => handleDeleteForever(t)}
            disabled={busyId === t.id}
            title="Permanently delete this archived form and its file (double confirm)"
            className="px-2 py-1 text-[11px] border border-red-300 text-red-600 rounded hover:bg-red-50 disabled:opacity-40"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-xl flex flex-col" style={{ height: '88vh' }}>
        {/* Header */}
        <div className="px-5 py-3.5 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-semibold text-lg">Form Library</h2>
            <p className="text-xs text-gray-500">
              Every form in the system ({templates.length} total, {archivedCount} archived{dupCount > 0 ? `, ${dupCount} possible duplicates` : ''}).
              Archive is reversible. Permanent delete only appears on archived forms — a live form can never be removed in one click.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2">✕</button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-2.5 border-b flex items-center gap-2 flex-shrink-0">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search forms…"
            className="flex-1 border rounded px-3 py-1.5 text-sm"
          />
          <div className="flex gap-1">
            {FILTERS.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-2.5 py-1.5 text-xs rounded ${
                  filter === f.id ? 'bg-blue-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <p className="text-center text-neutral-400 py-10 text-sm">Loading forms…</p>
          ) : visible.length === 0 ? (
            <p className="text-center text-neutral-400 py-10 text-sm">No forms match.</p>
          ) : (
            <>
              {dupGroups.length > 0 && (
                <div className="space-y-3">
                  {dupGroups.map((group, i) => (
                    <div key={i} className="border-2 border-dashed border-orange-300 bg-orange-50/40 rounded-xl p-2.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-orange-600 px-1 mb-1.5">
                        Possible duplicates ({group.length})
                      </div>
                      <div className="space-y-1.5">{group.map(renderRow)}</div>
                    </div>
                  ))}
                </div>
              )}
              {singles.length > 0 && (
                <div className="space-y-1.5">
                  {dupGroups.length > 0 && (
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400 px-1">Everything else</div>
                  )}
                  {singles.map(renderRow)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormLibrary;
